import { MonthlyData } from '../services/api';
import { useDashboardData } from '../hooks/useDashboardData';
import PortfolioEnvironmentalImpact from './PortfolioEnvironmentalImpact';
import ProjectTable from './ProjectTable';
import CircularInflowOutflow from './CircularInflowOutflow';
import Navigation from './Navigation';
import ProdiktLoginButton from './common/ProdiktLoginButton';
import TokenTester from './TokenTester';
import { isAuthenticationError } from '../utils/authUtils';

const ProjectPortfolioDashboardApp = () => {
  const {
    projects,
    organization,
    loading,
    error,
    selectedYear,
    setSelectedYear,
    availableYears,
    selectedYearData,
    selectedYearSummary,
  } = useDashboardData();

  const carbonImpactPerM2 = selectedYearSummary?.carbonImpactPerM2 || 0;
  
  const totalCO2 = selectedYearSummary?.totalCarbonImpact ? selectedYearSummary.totalCarbonImpact / 1000 : 0;

  const fillMissingMonthsPerSqm = (monthlyData: MonthlyData[], year: number) => {
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    return months.map(month => {
      const existingData = monthlyData.find(item => item.monthNumber === month);

      return {
        month: new Date(year, month - 1).toLocaleDateString('en-US', {
          month: 'short',
        }),
        started: existingData?.carbonImpactPerM2 || 0,
      };
    });
  };

  const fillMissingMonthsTotal = (monthlyData: MonthlyData[], year: number) => {
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    return months.map(month => {
      const existingData = monthlyData.find(item => item.monthNumber === month);
      return {
        month: new Date(year, month - 1).toLocaleDateString('en-US', {
          month: 'short',
        }),
        started: existingData?.totalCarbonImpact
          ? existingData.totalCarbonImpact / 1000
          : 0,
      };
    });
  };

  const chartData = fillMissingMonthsPerSqm(selectedYearData, selectedYear);
  const chartDataTotal = fillMissingMonthsTotal(selectedYearData, selectedYear);


  if (loading) {
    return (
      <div className="bg-[#fbfaf9] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-[#fbfaf9] min-h-screen flex items-center justify-center p-6">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-6">
            <div className="text-red-500 text-xl mb-4">
              ⚠️ Error Loading Data
            </div>
            <p className="text-gray-600">{error}</p>
            <div className="mt-4 space-x-2">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Retry
              </button>
              {isAuthenticationError(error) && <ProdiktLoginButton />}
            </div>
          </div>

          {/* Token Tester for Authentication Errors */}
          {isAuthenticationError(error) &&
            process.env.NODE_ENV === 'development' && (
              <TokenTester
                onTokenSent={(success, response) => {
                  console.log('Token sent:', { success, response });
                  if (success) {
                    // Refresh to retry authentication with new cookie
                    window.location.reload();
                  }
                }}
              />
            )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#fbfaf9] min-h-screen">
      {/* Navigation Bar */}
      <Navigation
        selectedYear={selectedYear}
        availableYears={availableYears}
        onYearChange={setSelectedYear}
        organizationName={organization?.name}
      />

      {/* Main Content */}
      <div className="p-6 space-y-6">
        {/* Section 1: Portfolio Environmental Impact */}
        <PortfolioEnvironmentalImpact
          avgCO2PerM2={carbonImpactPerM2}
          totalCO2={totalCO2}
          chartData={chartData}
          chartDataTotal={chartDataTotal}
        />

        {/* Section 2: Project Table */}
        <ProjectTable projects={projects} selectedYear={selectedYear} />

        {/* Section 3: Circular Inflow and Outflow */}
        <CircularInflowOutflow
          projects={projects}
          selectedYear={selectedYear}
        />
      </div>
    </div>
  );
};

export default ProjectPortfolioDashboardApp;
